/*
 * Header chrome enhancements:
 *   1. <header-cart-icon> - badge + aria-label react to `cart:updated`.
 *   2. Sticky-header scroll observer - sets [data-scrolled] on <header.header>
 *      when a 1px sentinel above the header leaves the viewport.
 *   3. Esc-to-close listener for <details.header__menu> mobile dropdown.
 *
 * Three responsibilities live in one file to keep the Phase 3 JS budget tight
 * (no third Custom Element file).
 */

// 1. Cart-icon Custom Element ----------------------------------------------
class HeaderCartIcon extends HTMLElement {
  constructor() {
    super();
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onLinkClick = this._onLinkClick.bind(this);
  }

  connectedCallback() {
    this.badge = this.querySelector('[data-cart-count]');
    this.link = this.querySelector('[data-cart-link]');
    document.addEventListener('cart:updated', this._onCartUpdated);
    if (this.link) this.link.addEventListener('click', this._onLinkClick);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._onCartUpdated);
    if (this.link) this.link.removeEventListener('click', this._onLinkClick);
  }

  _onCartUpdated(event) {
    // cart-store.js dispatches `{ cart }`; Phase 3 stub also accepted bare `{ item_count }`.
    // Support both shapes for forward/backward compatibility.
    const detail = (event && event.detail) || {};
    const cart = detail.cart || detail;
    const count = typeof cart.item_count === 'number' ? cart.item_count : 0;
    this.dataset.count = String(count);
    if (this.badge) {
      this.badge.textContent = String(count);
      if (count === 0) this.badge.setAttribute('hidden', '');
      else this.badge.removeAttribute('hidden');
    }
    if (this.link) {
      const label = count === 0
        ? this.link.dataset.labelEmpty || 'Cart, empty'
        : (this.link.dataset.labelTemplate || 'Cart, [[count]] items').replace('[[count]]', String(count));
      this.link.setAttribute('aria-label', label);
    }
  }

  _onLinkClick(e) {
    // Honour open-in-new-tab modifiers; let the browser navigate to /cart.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: false }));
  }
}

if (!customElements.get('header-cart-icon')) {
  customElements.define('header-cart-icon', HeaderCartIcon);
}

// 2. Sticky-header scroll listener ----------------------------------------
// Sets [data-scrolled] on <header.header> when window.scrollY > 0.
// (Sentinel + IntersectionObserver pattern won't work here because the
//  section wrapper is the sticky container; anything inside it sticks too.)
(function () {
  function init() {
    const header = document.querySelector('header.header[data-sticky="true"]');
    if (!header) return;
    let ticking = false;
    function update() {
      if (window.scrollY > 0) {
        header.setAttribute('data-scrolled', '');
      } else {
        header.removeAttribute('data-scrolled');
      }
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// 3. Header <details> menus: mobile panel + desktop dropdowns --------------
// Esc closes the menu holding focus and returns focus to its summary.
// Desktop dropdowns also close on outside click and when another opens.
(function () {
  const MENUS = 'details.header__menu[open], details.header__dropdown[open]';

  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = [...document.querySelectorAll(MENUS)].find((d) => d.contains(document.activeElement));
    if (!open) return;
    open.removeAttribute('open');
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  }

  function onClick(event) {
    document.querySelectorAll('details.header__dropdown[open]').forEach((d) => {
      if (!d.contains(event.target)) d.removeAttribute('open');
    });
  }

  document.addEventListener('keydown', onKeydown);
  document.addEventListener('click', onClick);
})();
